import React, { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import {
  getNativePushPermission,
  isNativePushConfigured,
  requestForToken,
  requestNativePushPermission,
} from "./firebase";
import { readNativeLocationPermission, requestNativeLocationPermission } from "./utils/nativePermissions";

const isNative = Capacitor.isNativePlatform();
const nativePushConfigured = isNativePushConfigured();

export default function DriverPermissions({ locationOnly = false, onLocationEnabled }) {
  const [pushState, setPushState] = useState("checking");
  const [locationState, setLocationState] = useState("checking");
  const [busy, setBusy] = useState("");
  const previousPush = useRef("checking");
  const previousLocation = useRef("checking");

  const refresh = useCallback(async () => {
    if (!isNative || !localStorage.getItem("token")) return;
    const [push, location] = await Promise.all([
      locationOnly ? Promise.resolve("unavailable") : getNativePushPermission(),
      readNativeLocationPermission(),
    ]);
    if (push === "granted" && !["granted", "checking"].includes(previousPush.current)) {
      // Permission may have been changed in Android Settings while app was away.
      requestForToken({ requestPermission: false });
    }
    if (location === "granted" && !["granted", "checking"].includes(previousLocation.current) &&
        typeof onLocationEnabled === "function") {
      onLocationEnabled();
    }
    previousPush.current = push;
    previousLocation.current = location;
    setPushState(push);
    setLocationState(location);
  }, [locationOnly, onLocationEnabled]);

  useEffect(() => {
    refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  if (!isNative || !localStorage.getItem("token")) return null;
  // A misconfigured APK must show a clear setup warning, not silently hide
  // notifications or imply that an Android permission alone enables FCM.
  const showPush = !locationOnly && pushState !== "checking" &&
    (pushState !== "granted" || !nativePushConfigured);
  const showLocation = locationState !== "checking" && locationState !== "granted";
  if (!showPush && !showLocation) return null;

  const requestPush = async () => {
    if (busy) return;
    setBusy("push");
    try {
      if (["prompt", "prompt-with-rationale"].includes(pushState)) {
        const permission = await requestNativePushPermission();
        if (permission === "granted") {
          await requestForToken({ requestPermission: false });
        }
      }
      await refresh();
    } finally {
      setBusy("");
    }
  };

  const requestLocation = async () => {
    if (busy) return;
    setBusy("location");
    try {
      await requestNativeLocationPermission();
      await refresh();
    } finally {
      setBusy("");
    }
  };

  const permissionRow = (type, state, title, description, action) => {
    const canRequest = state === "prompt" || state === "prompt-with-rationale";
    const pushNeedsSetup = type === "push" && !nativePushConfigured;
    const help = pushNeedsSetup && state === "granted"
      ? "مجوز اعلان فعال است؛ اما اتصال اعلان‌ها به Firebase در این نسخه تنظیم نشده است. برای دریافت اعلان، نسخه پیکربندی‌شده جدید را نصب کنید."
      : state === "denied"
        ? "دسترسی رد شده است. از تنظیمات گوشی ← برنامه‌ها ← مارانکو رانندگان ← مجوزها، آن را فعال کنید و به برنامه برگردید."
        : state === "unavailable"
          ? type === "push"
            ? "بررسی مجوز اعلان انجام نشد. برنامه را دوباره باز کنید و مجوز اعلان را در تنظیمات گوشی بررسی کنید."
            : "خدمات مکان‌یابی گوشی را روشن کنید یا مجوز برنامه را در تنظیمات بررسی کنید."
          : pushNeedsSetup
            ? "می‌توانید مجوز اعلان را فعال کنید، اما دریافت اعلان‌ها تا نصب نسخه متصل به Firebase ممکن نیست."
            : description;
    return (
      <div key={type} className="mk-driver-permission-row">
        <div className="mk-driver-permission-copy">
          <strong>{title}</strong>
          <p>{help}</p>
        </div>
        {!(pushNeedsSetup && state === "granted") && (
          <button type="button" disabled={Boolean(busy)} onClick={action}>
            {busy === type ? "در حال بررسی…" : canRequest ? "فعال‌سازی" : "بررسی مجدد"}
          </button>
        )}
      </div>
    );
  };

  return (
    <section className="mk-driver-permissions" dir="rtl" aria-label="مجوزهای اپلیکیشن">
      {showPush && permissionRow(
        "push", pushState, "اعلان سفرهای جدید",
        "با فعال‌کردن اعلان‌ها، درخواست‌های جدید سفر را دریافت می‌کنید.", requestPush
      )}
      {showLocation && permissionRow(
        "location", locationState, "موقعیت مکانی سفر",
        "برای ارسال موقعیت به مسافر در سفر فعال، دسترسی مکان را فعال کنید.", requestLocation
      )}
    </section>
  );
}
